<template>
  <div class="fob-about-me">
    <div class="columns">
      <!-- RESEARCH ACTIVITY DETAILS OF PERSON OR ORGANISATIONAL UNIT -->
      <div class="column">
        <div class="level">
          <!-- RESEARCH ACTIVITY | Title -->
          <div class="level-left">
            <div v-if="isLoading"
              class="is-size-5"
              style="margin-bottom: 10px; font-style: italic; font-variant: small-caps;"
            >
                <translate>AboutMe.loading</translate>
            </div>
            <div v-else
              class="fob-aboutme-heading is-size-5"
              style="max-width: 840px;"
            >
              <div>
                <span class="fob-aboutme-heading-label" style="font-weight: bold; font-variant: small-caps; margin-bottom: 10px;">{{ label }}</span>
                <span>({{leurisProfileFisPersid}})</span>
              </div>
              <span
                class="fob-only-for-print-view is-size-7"
              ><translate>{{activeResource.label}}</translate></span>
            </div>
          </div>
          <!-- RESEARCH ACTIVITY | Actions like share and print -->
          <div class="level-right fob-hide-in-print">
            <fob-aboutme-list-quick-actions
              :qa-share="true"
              :qa-share-compact-view="true"
              :qa-print="true"
            ></fob-aboutme-list-quick-actions>
          </div>
        </div>
        <!-- RESEARCH ACTIVITY | LEURIS PROFILE -->
        <div
          v-if="leurisProfile || leurisProfileFisPersid"
          class="leurisProfile fob-hide-in-print"
          style="margin-bottom: 2rem; padding: 0 8rem 0 2rem; border-left: 3px solid rgb(138, 193, 208);"
        >
          <!-- If a profile is available, display the possiblity empty abstract -->
          <div v-if="leurisProfile" v-html="leurisProfile.abstract[currentLocale]"></div>

          <!-- "No text available" and edit button only for user themself" -->
          <template v-if="userIsLoggedIn && (leurisProfileFisPersid === userFisPersid)">
            <div v-if="leurisProfile == null || (leurisProfile && !leurisProfile.abstract[currentLocale])">
              <em><translate>LeurisProfile.abstract.empty</translate></em>
            </div>
            <div style="margin: 1rem 0 1rem 0;">
              <b-icon icon="pencil" style="margin-right: 0.25rem;" size="is-small"></b-icon>
              <a :href="`${leurisProfileBaseUrl}/${leurisProfileFisPersid}/drafts/abstract`"><translate>LeurisProfile.abstract.edit</translate></a>
            </div>
          </template>
        </div>
        <!-- RESEARCH ACTIVITY | Tabs -->
        <div class="card" style="opacity: 0.95;">
          <b-tabs v-model="activeTab">
            <b-tab-item
              v-for="resourceType in resourceTypes"
              :key="resourceType.type"
              :disabled="resourceType.disabled"
              style="flex: 0 0 auto;"
            >
              <!-- RESEARCH ACTIVITY | Tabs | Label -->
              <template slot="header">
                <span :class="{'fob-is-filtered': hasActiveFilter(resourceType.type)}">
                  <translate>{{resourceType.label}}</translate>
                  <span>{{`(${total[resourceType.type]})`}}</span>
                </span>
              </template>
              <!-- RESEARCH ACTIVITY | Tabs | Content -->
              <div v-if="!resourceType.disabled">
                <fob-aboutme-list-view :resource-type="resourceType.type" style="height: 100%;"></fob-aboutme-list-view>
              </div>
            </b-tab-item>
          </b-tabs>
        </div>
      </div>
    </div>
    <!-- PRINT FOOTER -->
    <div class="print-footer">
      <span>
        <translate :translate-params="{printDate: printDate}">details.printedOn</translate>
      </span>
    </div>
    <!-- BACK TO TOP | @see https://github.com/caiofsouza/vue-backtotop -->
    <!--
    <back-to-top bottom="50px" right="50px">
      <button
        class="button is-primary fob-hide-in-print"
        style="height: 50px; width: 50px;border-radius: 50%; opacity: 0.8;"
      >
        <b-icon pack="fa" icon="chevron-up" style="height: auto; width: auto;"></b-icon>
      </button>
    </back-to-top>
    -->
  </div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex'
import { currentDateString } from '@/utils'
// import BackToTop from 'vue-backtotop'
import DateTimeHelper from '@/components/mixins/DateTimeHelper'
import DynamicTitle from '@/components/mixins/DynamicTitle'
import EntityHeader from '@/components/EntityHeader'
import AboutMeListView from '@/components/aboutme/AboutMeListView'
import AboutMeListQuickActions from '@/components/aboutme/AboutMeListQuickActions'

export default {
  name: 'fob-aboutme',
  mixins: [DynamicTitle, DateTimeHelper],
  data () {
    return {
      printDate: currentDateString(),
      leurisHead: {
        inner: 'leuris:Forschungsportal',
        complement: 'menu.aboutme'
      }
    }
  },
  computed: {
    ...mapGetters({
      filter: 'aboutme/getFilter',
      isLoading: 'aboutme/isLoading',
      label: 'aboutme/name',
      resourceTypes: 'aboutme/resourceTypes',
      selectedOrganisationalUnit: 'tree/selected',
      selected: 'publicView/getSelected',
      total: 'aboutme/getTotal',
      leurisProfile: 'aboutme/leurisProfile',
      leurisProfileFisPersid: 'aboutme/fisPersid',
      userFisPersid: 'user/fisPersid',
      userIsLoggedIn: 'user/isAuthorized'
    }),
    activeTab: {
      get () {
        return this.$store.getters['aboutme/getActiveTab']
      },
      set (activeTab) {
        this.$store.dispatch('aboutme/setActiveTab', activeTab)
      }
    },
    leurisProfileBaseUrl () {
      return process.env.MACO_PORT
        ? `${process.env.MACO_URL}:${process.env.MACO_PORT}`
        : `${process.env.MACO_URL}`
    },
    showOrgaTree () {
      return false
    },
    activeResource () {
      return this.resourceTypes[this.activeTab]
    },
    isCompactView () {
      if (this.$route.meta.compact || this.$route.query.compact === true || this.$route.query.compact === 'true') {
        return true
      } else if (this.$route.query.compact === null) {
        return false
      } else {
        return false
      }
    }
  },
  methods: {
    ...mapActions({
      fetchLeurisProfile: 'aboutme/fetchLeurisProfile',
      fetchResearchActivities: 'aboutme/fetchResearchActivities',
      fetchResearchSelection: 'aboutme/fetchResearchSelection'
    }),
    hasActiveFilter (resourceType) {
      let active = false
      if (Object.keys(this.filter[resourceType].filter).length > 0) {
        Object.keys(this.filter[resourceType].filter).some(filterKey => {
          let value = this.filter[resourceType].filter[filterKey]
          if (value && value.length > 0) {
            active = true
          }
        })
      }
      return active
    },
    setRoute (type, id) {
      let to = {
        name: 'public',
        params: {
          type,
          id,
          resourceType: this.activeResource.type
        }
      }

      if (this.isCompactView) {
        to.query = {
          compact: true
        }
      }
      this.$router.replace(to)
    }
  },
  watch: {
    selectedOrganisationalUnit () {
      // this.setRoute('ou', this.selectedOrganisationalUnit.id)
    }
  },
  mounted () {
    setInterval(() => {
      this.printDate = currentDateString()
    }, 30 * 1000)

    // load data
    this.fetchResearchSelection(this.userFisPersid)
    this.fetchLeurisProfile(this.userFisPersid)
    this.fetchResearchActivities()
  },
  components: {
    'fob-entity-header': EntityHeader,
    'fob-aboutme-list-view': AboutMeListView,
    'fob-aboutme-list-quick-actions': AboutMeListQuickActions
    // BackToTop
  }
}

</script>

<style lang="scss">
  .vue-back-to-top {
    z-index: 32 !important;
  }

  .fob-aboutme-select-divider {
    width: 350px;
    margin: 1rem 0;
    height: 1px;
    background-image: linear-gradient(
      to right,
      rgba(135, 194, 209, 0.1),
      rgba(138, 193, 208, 0.8),
      rgba(138, 194, 209, 0.1)
    );
  }

  .fob-is-filtered {
    font-style: italic;
  }

  .fob-is-filtered:after {
    content: '*'
  }

  .fob-aboutme-heading {
    .fob-aboutme-heading-label {
      margin: 0px;
      padding: 0px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/AboutMe.vue